/**
 * Block Control Component
 * Container for nested properties/fields
 */

import React, { useState } from 'react';
import { Box, Paper, Collapse, IconButton, Tooltip } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { ControlComponentProps } from '../../../../types/ControlTypes';
import { BlockSettings } from '../../../../types/ControlTypes';

// Will be imported dynamically to avoid circular dependency
let SchemaFormField: any = null;

// Lazy loader to avoid circular imports
const getSchemaFormField = async () => {
  if (!SchemaFormField) {
    const module = await import('../SchemaFormBuilder');
    SchemaFormField = module.SchemaFormField;
  }
  return SchemaFormField;
};

export const BlockControl: React.FC<ControlComponentProps> = ({
  name,
  path,
  value,
  onChange,
  error,
  disabled,
  readonly,
  label,
  description,
  required,
  control,
  schema,
  renderChild,
}) => {
  const settings = control.settings as BlockSettings;
  const [isCollapsed, setIsCollapsed] = useState(settings?.collapsed || false);
  const variant = settings?.variant || 'box';
  const isCollapsible = settings?.collapsible !== false;

  const childValue = typeof value === 'object' && value !== null ? value : {};

  const handleChildChange = (childKey: string, newValue: any) => {
    onChange({
      ...childValue,
      [childKey]: newValue,
    });
  };

  const renderChildren = () => {
    if (!settings?.childs) {
      return <Box sx={{ p: 2, color: '#999' }}>No child fields defined</Box>;
    }

    // childs can be an object (keyed dict) or an array — normalize to object
    const childsObj: Record<string, any> = Array.isArray(settings.childs)
      ? Object.fromEntries(
          settings.childs.map((child: any, i: number) => {
            const key = child.title ? child.title.toLowerCase().replace(/\s+/g, '_') : `field_${i}`;
            return [key, child];
          })
        )
      : settings.childs;

    return Object.entries(childsObj).map(([childKey, childSchema]: [string, any]) => {
      if (!renderChild) {
        return (
          <Box key={childKey} sx={{ mb: 2 }}>
            {/* Fallback: show placeholder */}
            <Box sx={{ p: 1, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              {childKey} (control not initialized yet)
            </Box>
          </Box>
        );
      }

      return (
        <Box key={childKey}>
          {renderChild({
            schema: childSchema,
            value: childValue[childKey],
            onChange: (newValue: any) => handleChildChange(childKey, newValue),
            fieldPath: `${path}.${childKey}`,
          })}
        </Box>
      );
    });
  };

  const headerSx =
    variant === 'section'
      ? { p: 1.5, backgroundColor: '#f0f0f0', borderBottom: '1px solid #e0e0e0' }
      : variant === 'card'
        ? { p: 1.5, backgroundColor: '#fafafa', borderBottom: '1px solid #e0e0e0' }
        : { p: 1.5, backgroundColor: '#f5f5f5', borderBottom: '1px solid #ddd' };

  return (
    <Box sx={{ mb: 2 }}>
      <Paper
        variant={variant === 'card' ? 'elevation' : 'outlined'}
        sx={{ overflow: 'hidden', borderRadius: variant === 'card' ? 2 : 1 }}
      >
        {/* Header */}
        <Box sx={{ ...headerSx, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ fontWeight: 700, color: '#333', fontSize: '15px' }}>
            {label || name}
            {required && <span style={{ color: 'red' }}> *</span>}
          </Box>
          {isCollapsible && (
            <Tooltip title={isCollapsed ? 'Expand' : 'Collapse'}>
              <IconButton
                size="small"
                onClick={() => setIsCollapsed(!isCollapsed)}
              >
                {isCollapsed ? <ExpandMore /> : <ExpandLess />}
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Content */}
        <Collapse in={!isCollapsed} timeout="auto" unmountOnExit={false}>
          <Box sx={{ p: 2 }}>
            {renderChildren()}
            {error && <Box sx={{ color: 'error.main', fontSize: '0.75rem', mt: 1 }}>{error}</Box>}
          </Box>
        </Collapse>
      </Paper>
    </Box>
  );
};

export default BlockControl;
